import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Save } from 'lucide-react';
import logger from '../../utils/logger';

export default function FinanceSettingsTab({ api }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'administrator';
  const [cfg, setCfg] = useState({ mileage_rate: 0.67, finance_approvers: [], ap_categories: [], ar_payers: [], expense_categories: [], budget_categories: [] });
  const [access, setAccess] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [c, a] = await Promise.all([api.get('/finance/config'), isAdmin ? api.get('/finance/access') : Promise.resolve({ data: [] })]);
      setCfg(c.data); setAccess(a.data);
    } catch (e) { logger.error(e); }
  }, [api, isAdmin]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put('/finance/config', cfg);
      toast.success('Settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const toggleAccess = async (userId, grant) => {
    try {
      await api.post('/finance/access', { user_id: userId, grant });
      toast.success(grant ? 'Access granted' : 'Access revoked');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const toggleApprover = async (userId) => {
    const current = cfg.finance_approvers || [];
    const next = current.includes(userId) ? current.filter(x => x !== userId) : [...current, userId];
    setCfg(c => ({ ...c, finance_approvers: next }));
    try {
      await api.put('/finance/config', { finance_approvers: next });
      toast.success(next.includes(userId) ? 'Approver added' : 'Approver removed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
      setCfg(c => ({ ...c, finance_approvers: current })); // revert
    }
  };

  const updateList = (key, value) => {
    setCfg(c => ({ ...c, [key]: value.split(',').map(s => s.trim()).filter(Boolean) }));
  };

  return (
    <div className="space-y-5">
      {isAdmin && (
        <Card className="border-[#6B3FA0] border-2 bg-gradient-to-br from-[#EDE7F6] to-white shadow-sm" data-testid="access-card">
          <CardContent className="p-4 space-y-3">
            <div>
              <h3 className="text-base font-extrabold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <span role="img" aria-label="key">🔑</span> Who can access the Finance Portal?
              </h3>
              <p className="text-xs text-[#5A4D6E] mt-1">Finance access is <strong>explicit for every user</strong> — including administrators. Flip the <strong>Access</strong> switch to grant a user into the finance silo. Flip <strong>Approver</strong> to let them approve travel reimbursements.</p>
            </div>
            <div className="divide-y divide-[#DED8EB] bg-white rounded-lg border border-[#DED8EB] px-3" data-testid="access-list">
              {access.length === 0 ? (
                <p className="text-sm text-[#8E85A0] py-4 text-center">Loading team…</p>
              ) : access.map(u => (
                <div key={u.id} className="flex items-center justify-between py-3 gap-3" data-testid={`access-row-${u.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-[#2D1B4E]">{u.name}</p>
                    <p className="text-[11px] text-[#8E85A0]">{u.email} · <span className="uppercase tracking-wide">{u.role}</span></p>
                  </div>
                  <div className="flex items-center gap-5 shrink-0">
                    <label className="flex flex-col items-center gap-0.5 cursor-pointer">
                      <span className="text-[10px] text-[#8E85A0] uppercase tracking-wider font-semibold">Access</span>
                      <Switch checked={u.has_access} onCheckedChange={v => toggleAccess(u.id, v)} data-testid={`access-switch-${u.id}`} />
                    </label>
                    <label className="flex flex-col items-center gap-0.5 cursor-pointer">
                      <span className="text-[10px] text-[#8E85A0] uppercase tracking-wider font-semibold">Approver</span>
                      <Switch checked={(cfg.finance_approvers || []).includes(u.id)} onCheckedChange={() => toggleApprover(u.id)} data-testid={`approver-switch-${u.id}`} />
                    </label>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-[#8E85A0] italic">Changes save automatically. Supervisors will see the "Finance" nav entry on their next page refresh.</p>
          </CardContent>
        </Card>
      )}

      <Card className="border-[#DED8EB] bg-white shadow-sm">
        <CardContent className="p-4 space-y-4">
          <div>
            <h3 className="text-sm font-bold text-[#2D1B4E] mb-3">Reimbursement Rate</h3>
            <div className="flex items-center gap-2">
              <Label className="text-sm">Mileage Rate ($/mi)</Label>
              <Input type="number" step="0.01" value={cfg.mileage_rate} onChange={e => setCfg(c => ({ ...c, mileage_rate: parseFloat(e.target.value) || 0 }))}
                className="w-28 border-[#DED8EB]" disabled={!isAdmin} data-testid="cfg-mileage-rate" />
              <span className="text-xs text-[#8E85A0]">IRS 2026 rate: $0.67</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-[#DED8EB] bg-white shadow-sm">
        <CardContent className="p-4 space-y-3">
          <h3 className="text-sm font-bold text-[#2D1B4E]">Dropdown Options</h3>
          <p className="text-xs text-[#8E85A0]">Comma-separated lists. Admin only.</p>
          <div><Label className="text-xs">AP Categories</Label><Input value={(cfg.ap_categories || []).join(', ')} onChange={e => updateList('ap_categories', e.target.value)} disabled={!isAdmin} className="border-[#DED8EB]" data-testid="cfg-ap-cats" /></div>
          <div><Label className="text-xs">AR Payers</Label><Input value={(cfg.ar_payers || []).join(', ')} onChange={e => updateList('ar_payers', e.target.value)} disabled={!isAdmin} className="border-[#DED8EB]" data-testid="cfg-ar-payers" /></div>
          <div><Label className="text-xs">Expense Categories</Label><Input value={(cfg.expense_categories || []).join(', ')} onChange={e => updateList('expense_categories', e.target.value)} disabled={!isAdmin} className="border-[#DED8EB]" data-testid="cfg-exp-cats" /></div>
          <div><Label className="text-xs">Budget Categories</Label><Input value={(cfg.budget_categories || []).join(', ')} onChange={e => updateList('budget_categories', e.target.value)} disabled={!isAdmin} className="border-[#DED8EB]" data-testid="cfg-budget-cats" /></div>
        </CardContent>
      </Card>

      {isAdmin && (
        <div>
          <Button onClick={saveConfig} disabled={saving} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-1 h-10" data-testid="cfg-save-btn">
            <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save Mileage Rate & Dropdown Options'}
          </Button>
        </div>
      )}
    </div>
  );
}
